import { hypot } from "yage/utils/deterministicMath";
import { System, SystemImpl } from "minecs";
import type { GameModel } from "yage/game/GameModel";
import { Transform } from "yage/schemas/entity/Transform";
import { Locomotion } from "yage/schemas/entity/Locomotion";
import { PlayerInput } from "yage/schemas/core/PlayerInput";
import { keyDown } from "yage/utils/keys";
import { AirportPath, AirportAgent, AirportPilot, AirportPassenger } from "../components/index.js";
import { CONFIG, CAPTAIN_WALK_SPEED, CAPTAIN_ENTRY_SPEED } from "../domain/config.js";
import { state, route } from "../services/world.js";
import { tier } from "../domain/airports.js";
@System(AirportPath, Transform, Locomotion)
export class AirportMovement extends SystemImpl<GameModel> {
    static depth = 2800;
    run = (g: GameModel, id: number) => {
        const p = g.getTypedUnsafe(AirportPath, id), t = g.getTypedUnsafe(Transform, id), v = g.getTypedUnsafe(Locomotion, id), e = state(g);
        v.x = 0;
        v.y = 0;
        if (!e.loaded)
            return;
        const agent = g.getTyped(AirportAgent, id);
        if (agent?.entering && !p.moving) agent.entering = false;
        const speed = agent?.entering ? CAPTAIN_ENTRY_SPEED : agent ? CAPTAIN_WALK_SPEED + e.boots * 0.4 : p.speed;
        if ((g.getTyped(AirportPilot, id)?.flyingGate ?? -1) >= 0) { p.moving = false; return; }
        if (agent?.inputLocked) {
            p.moving = false;
            return;
        }
        if (agent && !agent.entering) {
            const input = g.getTypedUnsafe(PlayerInput, id);
            const x = Number(keyDown(['d', 'right'], input.keyMap)) - Number(keyDown(['a', 'left'], input.keyMap));
            const y = Number(keyDown(['s', 'down'], input.keyMap)) - Number(keyDown(['w', 'up'], input.keyMap));
            if (x || y) {
                const n = hypot(x, y);
                v.x = Math.max(45 - t.x, Math.min(Math.min(CONFIG.width, tier(e).maxX) - 45 - t.x, x / n * speed));
                v.y = Math.max(359 - t.y, Math.min(CONFIG.height - 43 - t.y, y / n * speed));
                p.moving = false;
                p.points = [];
                agent.intent = -1;
                agent.building = -1;
                agent.dwell = 0;
                v.directionX = x / n;
                v.directionY = y / n;
                return;
            }
        }
        if (!p.moving)
            return;
        while (p.cursor + 1 < p.points.length && hypot(p.points[p.cursor] - t.x, p.points[p.cursor + 1] - t.y) < 3.5)
            p.cursor += 2;
        if (p.cursor >= p.points.length) {
            p.moving = false;
            if (agent) agent.entering = false;
            return;
        }
        const dx = p.points[p.cursor] - t.x, dy = p.points[p.cursor + 1] - t.y, d = hypot(dx, dy), step = Math.min(speed, d);
        const passenger = g.getTyped(AirportPassenger, id);
        if (passenger?.arriving && g.frame < passenger.releaseFrame) return;
        if ((passenger?.state === 0 || passenger?.state === 5 || passenger?.state === 3) && !passenger.arriving) {
            // Only the person immediately ahead controls the gap; passing traffic is free to cross.
            let ahead: number | undefined, serial = -1;
            for (const other of g.getComponentActives(AirportPassenger)) { // order-independent: max serial / any blocker
                const q = g.getTypedUnsafe(AirportPassenger, other);
                if (!q.arriving && q.stage === passenger.stage && q.state === passenger.state && (passenger.stage !== 6 || q.gate === passenger.gate) && (passenger.state === 0 ? passenger.queueOrder >= 0 && q.queueOrder >= 0 && (q.queueOrder < passenger.queueOrder || q.queueOrder === passenger.queueOrder && q.serial < passenger.serial) : q.serial < passenger.serial) && (passenger.state === 0 ? q.queueOrder : q.serial) > serial) {
                    ahead = other; serial = passenger.state === 0 ? q.queueOrder : q.serial;
                }
            }
            if (ahead !== undefined) {
                const front = g.getTypedUnsafe(Transform, ahead), fx = front.x - t.x, fy = front.y - t.y;
                if (hypot(fx, fy) < 42 && fx * dx + fy * dy > 0) return;
            }
        }
        if (passenger?.arriving) {
            // Older arrivals have priority at merges, so waiting can never form a cycle.
            for (const other of g.getComponentActives(AirportPassenger)) { // order-independent: max serial / any blocker
                const q = g.getTypedUnsafe(AirportPassenger, other);
                if (!q.arriving || q.serial >= passenger.serial || !g.getTypedUnsafe(AirportPath, other).moving) continue;
                const front = g.getTypedUnsafe(Transform, other), direction = g.getTypedUnsafe(Locomotion, other);
                const fx = front.x - t.x, fy = front.y - t.y;
                if (hypot(fx, fy) < 36 && fx * dx + fy * dy > 0 && direction.directionX * dx + direction.directionY * dy > d * 0.7) return;
            }
        }
        v.x = dx / d * step;
        v.y = dy / d * step;
        v.directionX = dx / d;
        v.directionY = dy / d;
        if (agent)
            agent.walked += step;
        if (Math.abs(t.x - p.lastX) + Math.abs(t.y - p.lastY) < 0.2)
            p.stuck++;
        else
            p.stuck = 0;
        p.lastX = t.x;
        p.lastY = t.y;
        if (p.stuck > 75) {
            route(g, id, { x: p.targetX, y: p.targetY }, true);
            p.stuck = 0;
        }
    };
}
